import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import ImageUploader from '../../components/app/ImageUploader';
import { GROUP_CATEGORIES, CATEGORY_EMOJI } from '../../constants/groups';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/api';
import AIDescriptionHelper from '../../components/app/AIDescriptionHelper';
import LocationInput from '../../components/app/LocationInput';
import { slugify } from '../../utils/slug';

type GroupType = 'community' | 'global' | 'private';

const inputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#f7f8fc',
  border: '1.5px solid #e2e6f0',
  borderRadius: '10px',
  padding: '0.875rem',
  color: '#1a1f36',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#4a5568',
  fontSize: '0.875rem',
  marginBottom: '0.5rem',
  fontWeight: 600,
};

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid #eef0f6',
  borderRadius: '16px',
  padding: '1.5rem',
  marginBottom: '1rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
};

export default function AppCreateGroup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const editCommunity = searchParams.get('community');
  const isEditMode = !!editId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [groupType, setGroupType] = useState<GroupType>('community');
  const [location, setLocation] = useState('');
  const [locationCoords, setLocationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [externalGroupLink, setExternalGroupLink] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [userCommunity, setUserCommunity] = useState('');

  useEffect(() => {
    apiGet<{ data: { community?: string; nationality?: string; city?: string } }>('/api_profile', undefined, true)
      .then(res => {
        const d = res.data;
        setUserCommunity(d.community || (d.nationality && d.city ? `${d.nationality} in ${d.city}` : ''));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    const params: Record<string, string> = { id: editId! };
    if (editCommunity) params.community = editCommunity;
    apiGet<{ data: any }>('/api_group_details', params, true)
      .then((res) => {
        const g = res.data;
        setName(g.name || '');
        setDescription(g.description || '');
        setCategory(g.category || '');
        setLocation(g.location || '');
        setImages(g.images || []);
        setExternalGroupLink(g.external_group_link || '');
        const streamId = g.stream_channel_id || '';
        const isPrivate = streamId.startsWith('group-p-') || g.is_private;
        const isGlobal = g.type === 'global';
        setGroupType(isPrivate ? 'private' : isGlobal ? 'global' : 'community');
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load group'))
      .finally(() => setLoadingEdit(false));
  }, [isEditMode, editId, editCommunity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Group name is required.'); return; }
    if (!description.trim()) { setError('Description is required.'); return; }
    if (!category) { setError('Please select a category.'); return; }
    if (!location.trim()) { setError('Location is required.'); return; }

    setSubmitting(true);
    setError(null);

    const payload: Record<string, unknown> = {
      name: name.trim(),
      description: description.trim(),
      category,
      type: groupType,
      location: location.trim(),
      ...(locationCoords ? { latitude: locationCoords.lat, longitude: locationCoords.lng } : {}),
      images,
    };
    if (externalGroupLink.trim()) payload.external_group_link = externalGroupLink.trim();

    try {
      if (isEditMode) {
        const params: Record<string, string> = { id: editId! };
        if (editCommunity) params.community = editCommunity;
        await apiPut('/api-update-group', payload, params);
        navigate(`/groups/${slugify(name)}-${editId}?community=${encodeURIComponent(editCommunity || '')}`);
      } else {
        const res = await apiPost<{ data?: { id: string }; id?: string }>('/api-save-group', payload, true);
        const newId = res.data?.id || res.id;
        navigate(newId ? `/groups/${slugify(name)}-${newId}` : '/groups');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} group.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this group? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const params: Record<string, string> = { id: editId! };
      if (editCommunity) params.community = editCommunity;
      await apiDelete('/api-delete-group', params);
      navigate('/groups');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete group.');
    } finally {
      setDeleting(false);
    }
  };

  if (loadingEdit) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fc' }}>
        <AppNav />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f8fc' }}>
      <AppNav />

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '1.75rem 1.25rem 4rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <button
            onClick={() => navigate(isEditMode ? `/groups/${editId}` : '/groups')}
            style={{ background: 'none', border: 'none', color: '#6b7a99', fontSize: '0.875rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 500 }}
          >
            ← {isEditMode ? 'Back to Group' : 'All Groups'}
          </button>
          {isEditMode && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{ background: 'none', border: '1.5px solid #fca5a5', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', cursor: 'pointer', padding: '0.35rem 0.75rem', fontWeight: 600, opacity: deleting ? 0.6 : 1 }}
            >
              {deleting ? 'Deleting...' : 'Delete Group'}
            </button>
          )}
        </div>

        <h1 style={{ color: '#1a1f36', fontWeight: 800, fontSize: '1.625rem', margin: '0 0 1.75rem', letterSpacing: '-0.03em' }}>
          {isEditMode ? 'Edit Group' : 'Create a Group'}
        </h1>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Basic Info</h2>

            <div style={{ marginBottom: '1.125rem' }}>
              <label style={labelStyle}>Group Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Marathi Community London" maxLength={100} style={inputStyle}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <label style={{ ...labelStyle, margin: 0 }}>Description <span style={{ color: '#ef4444' }}>*</span></label>
                <AIDescriptionHelper itemType="group" currentText={[name, description].filter(Boolean).join('\n')} onAccept={setDescription} />
              </div>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe your group's purpose, values, and who it's for..." rows={4} maxLength={2000}
                style={{ ...inputStyle, resize: 'vertical' }} onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
              <p style={{ color: '#8896ab', fontSize: '0.75rem', margin: '0.25rem 0 0', textAlign: 'right' }}>{description.length}/2000</p>
            </div>
          </div>

          {/* Category */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Category <span style={{ color: '#ef4444' }}>*</span></h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.5rem' }}>
              {GROUP_CATEGORIES.map((cat) => {
                const active = category === cat;
                return (
                  <button key={cat} type="button" onClick={() => setCategory(cat)}
                    style={{ padding: '0.625rem 0.5rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '10px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontSize: '0.82rem', fontWeight: active ? 700 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', transition: 'all 0.15s' }}>
                    <span>{CATEGORY_EMOJI[cat]}</span><span>{cat}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Visibility */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Visibility</h2>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {([
                { val: 'community', label: '🏘️ Community' },
                { val: 'global',    label: `🌍 Global in ${userCommunity?.split(' in ')[1] || 'your city'}` },
                { val: 'private',   label: '🔒 Private' },
              ] as { val: GroupType; label: string }[]).map(({ val, label }) => {
                const active = groupType === val;
                return (
                  <button key={val} type="button" onClick={() => setGroupType(val)}
                    style={{ flex: '1 1 140px', padding: '0.75rem', border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0', borderRadius: '10px', backgroundColor: active ? '#fff5eb' : '#f7f8fc', color: active ? '#FF385C' : '#4a5568', fontWeight: active ? 700 : 500, cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.15s', textAlign: 'center' }}>
                    {label}
                  </button>
                );
              })}
            </div>
            <p style={{ color: '#8896ab', fontSize: '0.78rem', margin: '0.75rem 0 0' }}>
              {groupType === 'community'
                ? `Community — Visible to "${userCommunity || 'your community'}"`
                : groupType === 'global'
                  ? `Global in ${userCommunity?.split(' in ')[1] || 'your city'} — Visible to everyone in your city`
                  : 'Private — Members must request to join and be approved.'}
            </p>
          </div>

          {/* Location */}
          <div style={sectionStyle}>
            <LocationInput value={location} onChange={setLocation} onCoordinates={(lat, lng) => setLocationCoords({ lat, lng })} placeholder="e.g. London, Amsterdam, Sydney..." required inputStyle={inputStyle} />
          </div>

          {/* External Link */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 0.375rem' }}>
              WhatsApp / Facebook Link <span style={{ color: '#8896ab', fontWeight: 400 }}>(optional)</span>
            </h2>
            <p style={{ color: '#8896ab', fontSize: '0.78rem', margin: '0 0 0.875rem' }}>Link to an existing WhatsApp group or Facebook group for this community.</p>
            <input type="url" value={externalGroupLink} onChange={(e) => setExternalGroupLink(e.target.value)} placeholder="https://chat.whatsapp.com/... or https://facebook.com/groups/..." style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#FF385C')} onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e6f0')} />
          </div>

          {/* Images */}
          <div style={sectionStyle}>
            <h2 style={{ color: '#1a1f36', fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem' }}>Images <span style={{ color: '#8896ab', fontWeight: 400 }}>(optional, up to 3)</span></h2>
            <ImageUploader value={images} onChange={setImages} maxImages={3} />
          </div>

          {error && (
            <div style={{ padding: '0.875rem 1rem', backgroundColor: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '10px', color: '#c53030', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting}
            style={{ width: '100%', padding: '1rem', backgroundColor: '#FF385C', border: 'none', borderRadius: '12px', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.75 : 1, transition: 'all 0.15s', boxShadow: '0 4px 16px rgba(255,56,92,0.3)' }}>
            {submitting ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes ✨' : 'Create Group ✨')}
          </button>
        </form>
      </div>
    </div>
  );
}
